import ObjectExport from './ObjectExport';
import { BORDER_OPA_COVER, BORDER_SIDE_FULL } from '../draw/borderGeometry';
import { splitColorOpa } from '../utils/color.js';
import { normalizeRangeValues } from './barGeometry.js';
import {
  getTemplatePreviewContextForObject,
  getTemplatePreviewSourceObject,
} from '../draw/templatePagePreview.js';

// border_width = 0;
// radius = 5;
// bg_color;         //bgcolor is hex8, per part (main, indicator, knob, …)
// border_color;     //color	Specifies the color of the border
// border_opa;       //uint8	Specifies opacity of the border
// border_width;     //uint8	Set the width of the border
// border_post;      //	bool	If true the border will be drawn after all children have been drawn.
// border_side = 15; //uint8	Specifies which sides of the border to draw.A sum of these values is also possible to select specific sides.
//                            0 = none
//                            1 = bottom
//                            2 = top
//                            4 = left
//                            8 = right
//                            15 = full

export default class HaspObject extends ObjectExport {

  attachedGuidelines = { vertical: null, horizontal: null };

  constructor(config) {
    super(config);

    this.onStyle('main', 'border_width', () => {
      const borderWidth = this.styleProp('main', 'border_width');
      // Use setStrokeWidth for CustomRect compatibility
      if (this.rect.setStrokeWidth) {
        this.rect.setStrokeWidth(borderWidth);
      } else {
        this.rect.strokeWidth(borderWidth);
      }
    });

    this.onStyle('main', 'border_color', () => {
      const rawColor = this.styleProp('main', 'border_color');
      const split = splitColorOpa(rawColor ?? '#000000ff');
      const hasAlpha = typeof rawColor === 'string' && rawColor.length > 7;
      // Use setStroke for CustomRect compatibility
      if (this.rect.setStroke) {
        this.rect.setStroke(split.color);
      } else {
        this.rect.stroke(split.color);
      }
      this.rect.borderOpa(hasAlpha
        ? Math.round(split.opacity * 255)
        : this.styleProp('main', 'border_opa') ?? BORDER_OPA_COVER);
    });

    // border_side / border_opa are handled here, on the shared base object, so
    // every widget gets LVGL-conformant partial borders with no widget-specific
    // code — the rendering itself lives in CustomRect/borderGeometry.
    this.onStyle('main', 'border_side', () => {
      this.rect.borderSide(this.styleProp('main', 'border_side') ?? BORDER_SIDE_FULL);
    });

    this.onStyle('main', 'border_opa', () => {
      const rawColor = this.styleProp('main', 'border_color');
      const hasAlpha = typeof rawColor === 'string' && rawColor.length > 7;
      this.rect.borderOpa(hasAlpha
        ? Math.round(splitColorOpa(rawColor).opacity * 255)
        : this.styleProp('main', 'border_opa') ?? BORDER_OPA_COVER);
    });

    this.onStyle('main', 'radius', () => {
      this.rect.cornerRadius(Math.round(this.styleProp('main', 'radius')));
      // The shadow and the outline both follow the object's corners, so radius
      // feeds them too.
      this.applyShadowStyle();
      this.applyOutlineStyle();
    });

    // LVGL outline — a closed ring outside the object, handled here on the
    // shared base object so every widget whose schema declares the properties
    // gets it with no widget-specific code. Like the shadow below, each change
    // re-reads the WHOLE resolved outline style: the geometry is computed from
    // all of them at once (pad shifts the ring, width changes the radius
    // clamp), so there is no meaningful per-property update. The rendering
    // itself lives in CustomRect/outlineGeometry.
    for (const prop of ['outline_width', 'outline_color', 'outline_opa', 'outline_pad']) {
      this.onStyle('main', prop, () => this.applyOutlineStyle());
    }

    // LVGL box shadow. Every one of these re-reads the WHOLE resolved shadow
    // style through applyShadowStyle() rather than poking one Konva attribute:
    // the LVGL mask is computed from all of them at once (spread changes the
    // radius clamp, width changes the extent, …), so there is no meaningful
    // per-property update. Konva's native shadowBlur is deliberately unused —
    // see src/draw/shadowGeometry.ts.
    for (const prop of ['shadow_width', 'shadow_spread', 'shadow_ofs_x', 'shadow_ofs_y', 'shadow_color', 'shadow_opa']) {
      this.onStyle('main', prop, () => this.applyShadowStyle());
    }
  }

  addItem(item) {
    this.top_haspid = item.haspid + 1;
    if (!this.isType('tabview') && item.isType('tab'))
      //tab object may only be added to tabview
      return;
    this.add(item);
    //update the pageZero overlay only if this Object is on pageZero
    if (this.isOnPageZero()) this.getLayer().updatePageZeroOverlay();
    return item;
  }

  isType(objType) {
    return this.obj === objType;
  }

  isContainer() {
    return (
      this.isType('page') ||
      this.isType('template_page') ||
      this.isType('obj') ||
      this.isType('tabview') ||
      this.isType('tab') ||
      this.isType('btnmatrix') ||
      this.isType('msgbox')
    );
  }

  /**
   * Read the current transient Designer template-preview context.
   * Ordinary page objects and template objects without an active source
   * return null. The context remains owned by HaspObjectLayer.
   */
  getTemplatePreviewContext() {
    return getTemplatePreviewContextForObject(this);
  }

  /** Read the current opener object without storing it on this object. */
  getTemplatePreviewSourceObject() {
    return getTemplatePreviewSourceObject(this);
  }

  /**
   * Return the configured value when a preview interaction has temporarily
   * overridden a property, otherwise the live value. Export/history code uses
   * this to keep preview-only action results out of persisted design state.
   */
  getConfiguredProperty(property) {
    return this._previewPropertyBase?.has(property)
      ? this._previewPropertyBase.get(property)
      : this[property];
  }

  /** Store a runtime-only preview override while leaving the original value recoverable. */
  setPreviewProperty(property, value) {
    if (!this._previewPropertyBase) {
      Object.defineProperty(this, '_previewPropertyBase', {
        configurable: true,
        value: new Map(),
      });
    }
    if (!this._previewPropertyBase.has(property)) this._previewPropertyBase.set(property, this[property]);
    this[property] = value;
  }

  /** Let a normal user edit turn a preview override into the configured value. */
  commitPreviewProperty(property) {
    if (!this._previewPropertyBase?.has(property)) return;
    this._previewPropertyBase.delete(property);
    if (!this._previewPropertyBase.size) delete this._previewPropertyBase;
  }

  withPreviewMutation(callback) {
    const layer = this.getLayer?.();
    const previousHistorySuppress = layer?._historySuppress;
    this._previewMutation = true;
    if (layer) layer._historySuppress = true;
    try {
      return callback();
    } finally {
      this._previewMutation = false;
      if (layer) layer._historySuppress = previousHistorySuppress;
    }
  }

  /** Atomically apply an openHASP Range pair for Bar, Slider and Arc. */
  setRangeValues(startValue, value) {
    const normalized = normalizeRangeValues({
      min: this.min,
      max: this.max,
      value,
      startValue,
    });
    if (!normalized) return false;
    this.start_value = normalized.startValue;
    this.val = normalized.value;
    if (typeof this.syncKnobFromValue === 'function') this.syncKnobFromValue();
    else if (typeof this.updateValueArc === 'function') this.updateValueArc();
    else if (typeof this.updateValue === 'function') this.updateValue();
    return true;
  }

  getBounds() {
    if (!this.rect) return { x: 0, y: 0, width: 0, height: 0 };
    const stage = this.getStage();
    return stage
      ? this.rect.getClientRect({ relativeTo: stage })
      : this.rect.getClientRect();
  }

  getSnapPoints() {
    const box = this.getBounds();
    return {
      vertical: [box.x, box.x + box.width / 2, box.x + box.width],
      horizontal: [box.y, box.y + box.height / 2, box.y + box.height],
    };
  }

  adjust(typeOrConfig, value) {
    // Support both adjust('type') and adjust({ type, value }) forms
    let type, val;
    if (typeOrConfig !== null && typeof typeOrConfig === 'object') {
      type = typeOrConfig.type;
      val = typeOrConfig.value;
    } else {
      type = typeOrConfig;
      val = value;
    }

    // Property editor changes are real design edits; emulator notifications
    // are wrapped in withPreviewMutation and must keep their runtime override.
    if (!this._previewMutation && typeof type === 'string') {
      const property = type.startsWith('data_')
        ? type.slice('data_'.length)
        : type.endsWith('_text') ? type.slice(0, -'_text'.length) : null;
      if (property) this.commitPreviewProperty(property);
    }

    if (type === 'node-haspid') this.idTextObj.text(this.haspid);

    if (!this._deferValueNormalization) {
      if (this.val < this.min) this.val = this.min;
      if (this.val > this.max) this.val = this.max;

      if (type === 'data_val' && ['btn', 'switch'].includes(this.obj)) {
        // console.log("clamp btn val", this.val);
        this.val = Math.max(0, Math.min(1, Number(this.val)));
      }
    }

    const event = new Event(type, {
      bubbles: true,
      cancelable: true,
      value: val
    });
    this.dispatchEvent(event);

    if (this.getLayer()) {

      this.getLayer().layerCallback(type, this)
      this.getLayer().updateObjectMenu(this);
      this.getLayer().requestRender();
    }
    this.idTextObj.moveToTop();
    this.visible(!this.hidden)
    //update the pageZero overlay if this Object is on pageZero
    if (this.isOnPageZero()) this.getLayer().updatePageZeroOverlay();
  }

}
